<template>
    <v-dialog :model-value="modelValue" max-width="720" @update:modelValue="$emit('update:modelValue', $event)">
        <v-card>
            <v-card-title>{{ isEdit ? 'Edit partner' : 'Add partner' }}</v-card-title>
            <v-form ref="formRef" @submit.prevent="save">
                <v-card-text>
                    <v-alert v-for="(e, i) in errors" :key="i" type="error" variant="tonal" class="mb-3">{{ e }}</v-alert>

                    <v-row>
                        <v-col cols="12" sm="4" class="py-1">
                            <v-text-field v-model="form.code" label="Code" density="compact"
                                          :rules="[vRules.required, vRules.maxLength(32)]"/>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-1">
                            <v-text-field v-model="form.name" label="Name" density="compact"
                                          :rules="[vRules.required, vRules.maxLength(100)]"/>
                        </v-col>

                        <v-col cols="12" sm="4" class="py-1">
                            <!-- Percent of every PAID service invoice. Capped at 100 to match
                                 the API: a partner cannot earn more than we collected. -->
                            <v-text-field v-model="form.commission_percent" label="Commission %" type="number"
                                          density="compact" min="0" max="100" step="0.01"
                                          :rules="[vRules.required, percentRule]"/>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-1">
                            <v-select v-model="form.payout_period" :items="payoutPeriods" label="Payout period"
                                      density="compact" :rules="[vRules.required]"/>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-1">
                            <v-switch v-model="form.status" color="primary" label="Active" density="compact"
                                      hide-details/>
                        </v-col>

                        <v-col cols="12" sm="4" class="py-1">
                            <v-text-field v-model="form.contact_name" label="Contact name" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-1">
                            <!-- The overnight assignment email goes here. Without it the
                                 partner is simply never told, so it is worth stating. -->
                            <v-text-field v-model="form.email" label="Contact email" density="compact"
                                          hint="Assignment and statement emails go here" persistent-hint/>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-1">
                            <v-text-field v-model="form.phone" label="Phone" density="compact"/>
                        </v-col>

                        <v-col cols="12" sm="6" class="py-1">
                            <v-text-field v-model="form.addr_1" label="Address line 1" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-1">
                            <v-text-field v-model="form.addr_2" label="Address line 2" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="3" class="py-1">
                            <v-text-field v-model="form.addr_city" label="City" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="3" class="py-1">
                            <v-text-field v-model="form.addr_state" label="State" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="3" class="py-1">
                            <v-text-field v-model="form.addr_postalcode" label="Postal code" density="compact"/>
                        </v-col>
                        <v-col cols="12" sm="3" class="py-1">
                            <v-text-field v-model="form.addr_country" label="Country" density="compact"/>
                        </v-col>
                        <v-col cols="12" class="py-1">
                            <v-text-field v-model="form.logo" label="Logo URL" density="compact"/>
                        </v-col>
                    </v-row>

                    <v-alert v-if="isEdit" type="info" variant="tonal" density="compact" class="mt-2">
                        Changing the rate affects future invoices only. Commission already earned keeps the
                        rate it was earned at.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="$emit('update:modelValue', false)">Cancel</v-btn>
                    <v-btn color="primary" type="submit" :loading="saving">Save</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';


const props = defineProps({
    modelValue: {type: Boolean, default: false},
    reseller: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue', 'saved']);

const {showSnackbar} = useSnackbarStore();

// Defined locally, as elsewhere in this project - there is no shared rules module.
const vRules = {
    required: (v) => (v !== null && v !== undefined && v !== '') || 'This field is required',
    maxLength: (n) => (v) => !v || String(v).length <= n || `Maximum ${n} characters`,
};

const payoutPeriods = [
    {title: 'Monthly', value: 'monthly'},
    {title: 'Quarterly', value: 'quarterly'},
    {title: 'Annual', value: 'annual'},
];

const formRef = ref(null);
const saving = ref(false);
const errors = ref([]);

const blank = () => ({
    code: '', name: '', logo: '',
    contact_name: '', email: '', phone: '',
    addr_1: '', addr_2: '', addr_city: '', addr_state: '', addr_postalcode: '', addr_country: '',
    commission_percent: 0, payout_period: 'monthly', status: true,
});

const form = ref(blank());
const isEdit = computed(() => !!props.reseller?.id);

const percentRule = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n) || n < 0 || n > 100) return 'Enter a percentage between 0 and 100';
    return true;
};

// Reset on every open so a cancelled edit never leaks into the next create.
watch(() => props.modelValue, (open) => {
    if (!open) return;
    errors.value = [];
    form.value = props.reseller ? {...blank(), ...props.reseller} : blank();
});

async function save() {
    const {valid} = await formRef.value.validate();
    if (!valid) return;

    saving.value = true;
    errors.value = [];
    try {
        const payload = {...form.value, commission_percent: parseFloat(form.value.commission_percent)};
        if (isEdit.value) {
            await send('put', `admin/resellers/${props.reseller.id}`, payload);
        } else {
            await send('post', 'admin/resellers', payload);
        }
        showSnackbar(isEdit.value ? 'Partner updated' : 'Partner created', 'success');
        emit('saved');
    } catch (error) {
        const data = error.response?.data;
        // Laravel validation comes back keyed by field; surface each so a duplicate
        // code says so instead of showing a bare "failed".
        if (data?.errors) {
            errors.value = Object.values(data.errors).flat();
        } else {
            errors.value.push(data?.message || 'Could not save partner.');
        }
    } finally {
        saving.value = false;
    }
}
</script>
